/**
 * @file CoreClass - Base class for entities with identity, hierarchy, logging,
 * and metrics.
 *
 * Every instance has a stable `id`, parent-child links via `adopt()` (logs
 * bubble up with path prefixes; metrics bubble up carrying `id` + `component`
 * labels while the name stays fixed), structured logging at debug/info/
 * warn/error, Prometheus-style metrics (counter/gauge/histogram/timer), and
 * the resilience helpers `sleep` / `withRetry` / `withTimeout`.
 *
 * @example
 * ```typescript
 * class Server extends CoreClass<{ id: string }> {
 *   addConnection(conn: Connection): void {
 *     this.adopt(conn)
 *     this.gauge('connections', this.connections.size)
 *   }
 * }
 * ```
 *
 * @module
 */

import { CoreError } from './core_error.ts'
import {
  LOG_LEVEL_VALUES,
  metricName,
  parseMetricName,
  type EventName,
  type LogLevel,
  type MetricLabels,
  type MetricName,
  type MetricType,
  type ValidatedLabels,
  type ValidEvent,
  type ValidName,
} from './core_telemetry.ts'

/**
 * Framework-reserved event channels every CoreClass emits/listens on
 * internally. Subclasses' declared `TEvents` maps merge with this set
 * so the adopt-bubble walk's `child.on('log', …)` / `child.on('metric',
 * …)` registration type-checks regardless of whether a subclass has
 * declared its own event map.
 *
 * Subclass `TEvents` maps must not redefine `log` / `metric` /
 * `newListener` / `removeListener` — `EffectiveEvents` resolves
 * framework keys first, so a stray subclass declaration is silently
 * shadowed (and the subclass's bubble walk would break in confusing
 * ways).
 */
export type CoreInternalEvents = {
  log: [LogLevel, string, object | undefined, ...CoreClass[]]
  metric: [MetricType, string, number, MetricLabels]
  newListener: [string, (...args: unknown[]) => void]
  removeListener: [string, (...args: unknown[]) => void]
}

// Metric names hoisted to module scope for the resilience helpers so the hot
// path doesn't re-validate on every invocation.
const RETRY_ATTEMPT_TOTAL = metricName('retry.attempt_total')
const RETRY_BACKOFF_MS = metricName('retry.backoff_ms')
const TIMEOUT_TOTAL = metricName('timeout_total')

/**
 * Argument type for metric-emitting methods. For a string literal `S`,
 * `ValidName<S>` enforces the Prometheus naming rules at compile time;
 * pre-branded names built via `metricName(...)` / `parseMetricName(...)` are
 * accepted directly. Internal bubble paths receive arbitrary `string` via a
 * runtime escape hatch on the bus event.
 */
type MetricNameArg<S extends string> = ValidName<S> | MetricName

/**
 * Abstract base for every LabCraft entity. Provides identity (`id`, `fullId`),
 * parent-child hierarchy (`adopt`, `children`, `subtree`), structured logging
 * (`debug`/`info`/`warn`/`error`), Prometheus metrics (`counter`/`gauge`/
 * `histogram`/`timer`), and the resilience helpers `sleep` / `withRetry` /
 * `withTimeout`. Logs and metrics emitted by adopted children bubble up the
 * parent chain with path prefixes; `warn` / `error` require a `CoreError`.
 *
 * @typeParam TProps - Constructor props; must include an optional `id`.
 */
export abstract class CoreClass<
  TProps extends { id?: string } = { id?: string },
  TEvents extends Record<string, readonly unknown[]> = {},
> {
  /**
   * Global log-level threshold — the floor `getEffectiveLevel()` returns
   * for an instance with no `_logLevel` and no ancestor that sets one
   * (parentless `CoreClass` instances: value objects, helpers, tests).
   * Defaults to `info`; assign to override (e.g. the mocha test setup, a
   * `Program` honoring `<PREFIX>_LOG_LEVEL`).
   */
  static globalLogLevel: number = LOG_LEVEL_VALUES.info

  /**
   * Registry of all live CoreClass instances. WeakRef + a
   * FinalizationRegistry allow GC of unreferenced instances; dead refs are
   * also pruned lazily during `getInstances()` iteration so the set never
   * leaks across long-lived processes.
   */
  static readonly instances = new Set<WeakRef<CoreClass>>()

  /** May be undefined in environments without `FinalizationRegistry` (Hermes). */
  private static readonly finalizer:
    | FinalizationRegistry<WeakRef<CoreClass>>
    | undefined =
    typeof FinalizationRegistry !== 'undefined'
      ? new FinalizationRegistry((ref: WeakRef<CoreClass>) => {
          CoreClass.instances.delete(ref)
        })
      : undefined

  /** All live CoreClass instances. Prunes dead WeakRefs during iteration. */
  static getInstances(): CoreClass[] {
    const live: CoreClass[] = []
    for (const ref of CoreClass.instances) {
      const instance = ref.deref()
      if (instance) {
        live.push(instance)
      } else {
        CoreClass.instances.delete(ref)
      }
    }
    return live
  }

  /**
   * Recursively freeze a plain-object/array tree. Only plain objects (Object
   * or null prototype) and arrays are frozen; class instances, functions, and
   * primitives are returned as-is. Circular references are guarded via
   * `seen`. Called automatically by the constructor on `props`.
   */
  static deepFreeze<T>(obj: T, seen = new Set<object>()): Readonly<T> {
    if (obj === null || typeof obj !== 'object' || typeof obj === 'function') {
      return obj as Readonly<T>
    }
    if (Object.isFrozen(obj)) return obj as Readonly<T>
    if (seen.has(obj as object)) return obj as Readonly<T>
    const proto = Object.getPrototypeOf(obj)
    if (proto !== Object.prototype && proto !== null && !Array.isArray(obj)) {
      return obj as Readonly<T>
    }
    seen.add(obj as object)
    Object.freeze(obj)
    for (const val of Object.values(obj as object)) {
      CoreClass.deepFreeze(val, seen)
    }
    return obj as Readonly<T>
  }

  readonly id: string
  protected readonly props: Readonly<TProps>

  private events = new Map()
  private _parent: CoreClass | undefined = undefined
  private _logLevel: number | undefined = undefined

  /**
   * @param props - Configuration; `id` defaults to the constructor name
   *   lowercased. Props are deep-frozen and stored as `this.props`.
   * @throws CoreError(`UNEXPECTED`) if instantiated as the abstract class.
   */
  constructor(props: TProps) {
    if (this.constructor === CoreClass) {
      throw new CoreError({
        code: 'UNEXPECTED',
        message: 'Cannot instantiate an abstract class!',
      })
    }

    this.id = props.id ?? this.constructor.name.toLowerCase()
    this.props = CoreClass.deepFreeze(props)

    const ref = new WeakRef(this)
    CoreClass.instances.add(ref)
    CoreClass.finalizer?.register(this, ref)
  }

  /** Parent instance if this was adopted, undefined otherwise. */
  get parent(): CoreClass | undefined {
    return this._parent
  }

  /** Full hierarchical ID from root to this instance (e.g. 'api.db.conn-1'). */
  get fullId(): string {
    return this._parent ? `${this._parent.fullId}.${this.id}` : this.id
  }

  /** Direct children of this instance (adopted via `adopt()`). */
  get children(): CoreClass[] {
    return CoreClass.getInstances().filter(i => i._parent === this)
  }

  /** This instance and all descendants (breadth-first). */
  get subtree(): CoreClass[] {
    const result: CoreClass[] = []
    const queue: CoreClass[] = [this]

    while (queue.length > 0) {
      const current = queue.shift()!
      result.push(current)
      queue.push(...current.children)
    }

    return result
  }

  /** True if this instance has an explicit log level override. */
  hasLogLevelOverride(): boolean {
    return this._logLevel !== undefined
  }

  /** Effective log level (may be inherited from the parent chain). */
  getLogLevel(): LogLevel {
    const level = this.getEffectiveLevel()
    return (
      (Object.entries(LOG_LEVEL_VALUES).find(
        ([, v]) => v === level,
      )?.[0] as LogLevel) ?? 'info'
    )
  }

  /** Set log level for this instance. Use `'inherit'` to clear the override. */
  setLogLevel(level: LogLevel | 'inherit'): void {
    this._logLevel = level === 'inherit' ? undefined : LOG_LEVEL_VALUES[level]
  }

  /**
   * Apply `level` to this instance and every descendant.
   *
   * @returns `fullId`s of affected instances.
   */
  setSubtreeLogLevel(level: LogLevel | 'inherit'): string[] {
    const affected: string[] = []
    for (const instance of this.subtree) {
      instance.setLogLevel(level)
      affected.push(instance.fullId)
    }
    return affected
  }

  /** Effective log level as a number, walking up the parent chain. */
  getEffectiveLevel(): number {
    if (this._logLevel !== undefined) return this._logLevel
    if (this._parent) return this._parent.getEffectiveLevel()
    return CoreClass.globalLogLevel
  }

  /** JSON representation; subclasses override to surface additional state. */
  toJSON(): any {
    return { id: this.id }
  }

  /** Used by `util.inspect`; mirrors `toJSON()`. */
  [Symbol.for('nodejs.util.inspect.custom')](): any {
    return this.toJSON()
  }

  // ── EventEmitter ─────────────────────────────────────────────────────────
  //
  // Typed via paired overloads:
  //   1. Framework-reserved events (`log` / `metric` / `newListener` /
  //      `removeListener`) — concrete shapes from `CoreInternalEvents`.
  //   2. Subclass-declared events from `TEvents` — typed payloads.
  //
  // Subclasses with no `TEvents` map have access to framework events
  // only — emitting an undeclared event is a type error (declare it in
  // `TEvents` or remove the call). The trailing implementation
  // signature is internal and not callable from outside.

  /** Emit `eventName` to all listeners. Returns `true` if any fired. */
  emit<E extends keyof CoreInternalEvents>(
    eventName: E,
    ...args: CoreInternalEvents[E]
  ): boolean
  emit<E extends keyof TEvents & string>(
    eventName: E,
    ...args: TEvents[E]
  ): boolean
  emit(eventName: string, ...args: any[]): boolean {
    const { events } = this
    if (!events.has(eventName)) return false

    const listeners = events.get(eventName)
    const result = listeners.size !== 0

    for (const listener of listeners) {
      listener.call(this, ...args)
    }

    return result
  }

  /** Register a listener for `eventName`. Returns `this` for chaining. */
  on<E extends keyof CoreInternalEvents>(
    eventName: E,
    listener: (...args: CoreInternalEvents[E]) => void,
  ): this
  on<E extends keyof TEvents & string>(
    eventName: E,
    listener: (...args: TEvents[E]) => void,
  ): this
  on(eventName: string, listener: (...args: any[]) => void): this {
    const { events } = this

    events.has(eventName) || events.set(eventName, new Set())
    this.emit(
      'newListener',
      eventName,
      listener as (...args: unknown[]) => void,
    )

    const listeners = events.get(eventName)
    listeners.add(listener)

    return this
  }

  /** Register a one-shot listener for `eventName`. */
  once<E extends keyof CoreInternalEvents>(
    eventName: E,
    listener: (...args: CoreInternalEvents[E]) => void,
  ): this
  once<E extends keyof TEvents & string>(
    eventName: E,
    listener: (...args: TEvents[E]) => void,
  ): this
  once(eventName: string, listener: (...args: any[]) => void): this {
    const { events } = this

    events.has(eventName) || events.set(eventName, new Set())
    this.emit(
      'newListener',
      eventName,
      listener as (...args: unknown[]) => void,
    )

    const listeners = events.get(eventName)
    const wrapper = function (...args: any[]) {
      listeners.delete(wrapper)
      if (listeners.size === 0) events.delete(eventName)
      listener(...args)
    }
    listeners.add(wrapper)

    return this
  }

  /** Remove `listener` from `eventName`. */
  off<E extends keyof CoreInternalEvents>(
    eventName: E,
    listener: (...args: CoreInternalEvents[E]) => void,
  ): this
  off<E extends keyof TEvents & string>(
    eventName: E,
    listener: (...args: TEvents[E]) => void,
  ): this
  off(eventName: string, listener: (...args: any[]) => void): this {
    const { events } = this

    if (events.has(eventName)) {
      const listeners = events.get(eventName)
      listeners.delete(listener)
      if (listeners.size === 0) events.delete(eventName)
      this.emit(
        'removeListener',
        eventName,
        listener as (...args: unknown[]) => void,
      )
    }

    return this
  }

  /** Alias of `on()` — Node `EventEmitter` parity. */
  addListener<E extends keyof CoreInternalEvents>(
    eventName: E,
    listener: (...args: CoreInternalEvents[E]) => void,
  ): this
  addListener<E extends keyof TEvents & string>(
    eventName: E,
    listener: (...args: TEvents[E]) => void,
  ): this
  addListener(eventName: string, listener: (...args: any[]) => void): this {
    return this.on(eventName as keyof CoreInternalEvents, listener)
  }

  /** Remove all listeners for `eventName`, or every event if omitted. */
  removeAllListeners(eventName?: string): this {
    const { events } = this

    if (eventName === undefined) {
      for (const k of Array.from(events.keys())) {
        this.removeAllListeners(k as string)
      }
      return this
    }

    if (events.has(eventName)) {
      const listeners = events.get(eventName)
      events.delete(eventName)

      for (const listener of listeners) {
        this.emit(
          'removeListener',
          eventName,
          listener as (...args: unknown[]) => void,
        )
      }
    }

    return this
  }

  /** Alias of `off()` — Node `EventEmitter` parity. */
  removeListener<E extends keyof CoreInternalEvents>(
    eventName: E,
    listener: (...args: CoreInternalEvents[E]) => void,
  ): this
  removeListener<E extends keyof TEvents & string>(
    eventName: E,
    listener: (...args: TEvents[E]) => void,
  ): this
  removeListener(eventName: string, listener: (...args: any[]) => void): this {
    return this.off(eventName as keyof CoreInternalEvents, listener)
  }

  /** Event names with at least one registered listener. */
  eventNames() {
    const { events } = this
    return Array.from(events.keys())
  }

  /**
   * Listeners for `eventName`. Subtly different from Node `EventEmitter`:
   * `once()` wrappers are returned, not the unwrapped originals.
   */
  listeners(eventName: any) {
    const { events } = this
    return events.has(eventName) ? Array.from(events.get(eventName)) : []
  }

  // ── Logging ──────────────────────────────────────────────────────────────
  //
  // Emitted as ('log', level, event, context, ...hierarchy). As logs bubble
  // up via adopt(), each parent prepends its id to the event path and appends
  // itself to the hierarchy array; the resulting log line carries the full
  // path (for source navigation) plus the state of every ancestor (via
  // toJSON()). A root hop (adoptAsRoot) forwards the event path unchanged so
  // the root service's own instance id leads and the daemon type is dropped
  // (see adoptAsRoot). `warn` / `error` require a CoreError so structured
  // cause chains and metadata reach the log sink.

  // Event-name type parameter shape — string-literal call sites flow through
  // `ValidEvent<E>` (compile-time camelCase + dot-segments validator);
  // pre-branded `EventName` values (from `parseEventName(s)`) flow through
  // unchanged. The branded path is what dynamic call sites (`${verb}.${noun}`)
  // use when they can't satisfy the literal template-literal validator.

  /** Logs at the debug level. */
  debug<E extends string>(event: ValidEvent<E> | EventName, context?: object) {
    if (!this.shouldLog(LOG_LEVEL_VALUES.debug)) return
    this.emit(
      'log',
      'debug',
      `${this.id}.${event}`,
      CoreClass.normalizeContext(context),
      this,
    )
  }

  /** Logs at the info level. */
  info<E extends string>(event: ValidEvent<E> | EventName, context?: object) {
    if (!this.shouldLog(LOG_LEVEL_VALUES.info)) return
    this.emit(
      'log',
      'info',
      `${this.id}.${event}`,
      CoreClass.normalizeContext(context),
      this,
    )
  }

  /** Logs at the warn level. `error` carries the structured cause chain. */
  warn<E extends string>(
    event: ValidEvent<E> | EventName,
    error: CoreError,
    context?: object,
  ) {
    if (!this.shouldLog(LOG_LEVEL_VALUES.warn)) return
    // Normalize before the spread so a CoreClass-typed context contributes
    // its public `toJSON()` surface (not raw private fields like `_state`,
    // `_logLevel`, `events`). `error.toJSON()` last ensures
    // causeChain/rootCause cannot be overwritten.
    const normalized = CoreClass.normalizeContext(context)
    const mergedContext = { ...normalized, ...error.toJSON() }
    this.emit('log', 'warn', `${this.id}.${event}`, mergedContext, this)
  }

  /** Logs at the error level. `error` carries the structured cause chain. */
  error<E extends string>(
    event: ValidEvent<E> | EventName,
    error: CoreError,
    context?: object,
  ) {
    if (!this.shouldLog(LOG_LEVEL_VALUES.error)) return
    const normalized = CoreClass.normalizeContext(context)
    const mergedContext = { ...normalized, ...error.toJSON() }
    this.emit('log', 'error', `${this.id}.${event}`, mergedContext, this)
  }

  /**
   * Coerce a log-method `context` arg into a plain serialisable object.
   * `CoreClass` instances pass through their `toJSON()` so the LogEntry's
   * context iterates the class's curated public surface, not raw private
   * fields (`_state`, `_logLevel`, `_parent`, `events`) that `Object.entries`
   * would otherwise pick up in the sink. Plain objects (call-site deltas like
   * `{ port: 8080 }`) and `undefined` pass through unchanged.
   *
   * The umbrella's open decision on convention vs coercion (#317) keeps this
   * as defence-in-depth: even once #317's Phase 3 rewrites every
   * `this.info('event', this)` call site to a hand-picked delta, the
   * coercion stays so any future reintroduction of `this`-as-context can't
   * silently leak private fields back into the LogEntry schema.
   */
  private static normalizeContext(
    context: object | undefined,
  ): object | undefined {
    return context instanceof CoreClass ? context.toJSON() : context
  }

  // ── Metrics ──────────────────────────────────────────────────────────────
  //
  // Emitted as ('metric', type, name, value, labels). The `name` is the
  // declared metric string, unchanged as it bubbles — hierarchy identity
  // rides in *labels*, not in the name (Prometheus/VictoriaMetrics
  // convention: the name says what is measured; labels say which instance /
  // component). As a metric bubbles via adopt(), each hop prepends its
  // segment to the `component` label (see adoptWithPrefix()); the instance
  // `id` label is applied once at the daemon's MetricsSink (the export
  // boundary), not during the bubble — see backend/sinks/metrics.ts.
  //
  // `name` is type-checked: string literals validate against Prometheus
  // naming rules via ValidName; pre-branded names from `metricName(...)` /
  // `parseMetricName(...)` flow through unchanged.

  /** Record a counter increment (default 1). Monotonically increasing. */
  counter<S extends string, L extends MetricLabels = MetricLabels>(
    name: MetricNameArg<S>,
    labels?: L & ValidatedLabels<L>,
    value = 1,
  ) {
    if (!this.shouldEmitMetric()) return
    this.emit('metric', 'counter', name, value, labels ?? {})
  }

  /** Set a gauge value. Gauges go up or down (e.g. current connections). */
  gauge<S extends string, L extends MetricLabels = MetricLabels>(
    name: MetricNameArg<S>,
    value: number,
    labels?: L & ValidatedLabels<L>,
  ) {
    if (!this.shouldEmitMetric()) return
    this.emit('metric', 'gauge', name, value, labels ?? {})
  }

  /** Record a histogram observation (distributions, durations, sizes). */
  histogram<S extends string, L extends MetricLabels = MetricLabels>(
    name: MetricNameArg<S>,
    value: number,
    labels?: L & ValidatedLabels<L>,
  ) {
    if (!this.shouldEmitMetric()) return
    this.emit('metric', 'histogram', name, value, labels ?? {})
  }

  /**
   * Start a timer that emits a histogram when the returned function is
   * called. Capture the branded name once so the closure can emit without
   * re-validating.
   *
   * @example
   * ```ts
   * const done = this.timer('handleRequest.duration')
   * await processRequest()
   * done({ status: 200 })  // records elapsed ms
   * ```
   */
  timer<S extends string>(
    name: MetricNameArg<S>,
  ): <L extends MetricLabels = MetricLabels>(
    labels?: L & ValidatedLabels<L>,
  ) => void {
    const start = performance.now()
    const histogramName = name as MetricName
    return <L extends MetricLabels = MetricLabels>(
      labels?: L & ValidatedLabels<L>,
    ) => {
      this.histogram(histogramName, performance.now() - start, labels)
    }
  }

  // ── Utilities ────────────────────────────────────────────────────────────

  /**
   * Adopt a child, establishing a parent-child relationship. The child's
   * logs and metrics then bubble up to this parent: log event paths gain
   * this id as a prefix; metric `component` labels gain this child's id as a
   * path segment (the metric *name* is never prefixed). The child's
   * effective log level inherits from this parent unless overridden.
   *
   * @returns The child instance (for chaining).
   */
  protected adopt<T extends CoreClass>(child: T): T {
    return this.adoptWithPrefix(child, false)
  }

  /**
   * Adopt a child as a **root service** of this daemon. Two effects, one per
   * telemetry channel:
   *
   * - **Logs:** the child's event path is forwarded **unchanged** — the
   *   child's own `id` leads (`account0.onSignIn`; its descendants
   *   `account0.github.onSignIn`). The daemon *type* is intentionally NOT in
   *   the log path (drop-the-type): `job=<type>` from the scrape and the
   *   `lc-<type>.service` systemd unit already carry it, and an `account*`
   *   wildcard filters across instances. Contrast standard `adopt`, which
   *   prepends this node's `id`.
   * - **Metrics:** marks the instance boundary — the child's **class name**
   *   (lowercased) becomes the top `component` segment (a metric's node is
   *   `component=account` / `component=account.github`) while the daemon's
   *   `id` label carries the instance identity (`account0`). Node-uniqueness
   *   lives in `component`, so the className stays here even though the log
   *   path drops it.
   *
   * Used by `Daemon` to adopt user-services produced by the services
   * factory; framework-side adopts (telemetry, http listeners) stay on
   * regular `adopt`. See `.agents/rules/services.md` § "Event-name
   * navigation".
   *
   * @returns The child instance (for chaining).
   */
  protected adoptAsRoot<T extends CoreClass>(child: T): T {
    return this.adoptWithPrefix(child, true)
  }

  /**
   * Shared bubble-walk wiring used by both `adopt` (`isRoot=false`) and
   * `adoptAsRoot` (`isRoot=true`).
   *
   * The **log** path: a non-root hop prepends this node's `id`
   * (`${this.id}.${event}`) — the method-addressable navigation shape from
   * services.md. A root hop forwards the event **unchanged**, so the root
   * service's own instance id leads the path and the daemon type is dropped.
   *
   * The **metric** path keeps the metric *name* intact and builds a
   * `component` label from the hierarchy: each hop prepends its segment (the
   * child's class name at a root hop, else the child's id). The instance
   * `id` label is applied once at the daemon's MetricsSink (the export
   * boundary), not here — the bubble only assembles `component`.
   */
  private adoptWithPrefix<T extends CoreClass>(child: T, isRoot: boolean): T {
    child._parent = this

    child.on(
      'log',
      (
        level: LogLevel,
        event: string,
        context: object | undefined,
        ...hierarchy: CoreClass[]
      ) => {
        if (!this.shouldLog(LOG_LEVEL_VALUES[level])) return
        const path = isRoot ? event : `${this.id}.${event}`
        this.emit('log', level, path, context, ...hierarchy, this)
      },
    )

    child.on(
      'metric',
      (type: MetricType, name: string, value: number, labels: MetricLabels) => {
        if (!this.events.has('metric')) return

        // Build the `component` label: a root hop contributes the child's
        // class name as the top segment, every other hop the child's id. The
        // instance `id` is stamped later, at the daemon's MetricsSink — the
        // bubble only assembles the component path.
        const segment = isRoot ? child.constructor.name.toLowerCase() : child.id
        const component = labels.component
          ? `${segment}.${labels.component}`
          : segment
        this.emit('metric', type, name, value, { ...labels, component })
      },
    )

    return child
  }

  /** Pause execution for `ms` milliseconds. */
  protected async sleep(ms: number): Promise<void> {
    return new Promise(resolve => {
      setTimeout(resolve, ms)
    })
  }

  /**
   * Retry `operation` with exponential backoff and jitter.
   *
   * @param options.minBackoff - Initial backoff in ms (default 1000).
   * @param options.maxBackoff - Backoff cap in ms (default 60000).
   * @param options.maxAttempts - Retry cap; `Infinity` for unlimited (default
   *   `Infinity`).
   * @param options.jitter - Randomness factor 0–1 to prevent thundering herd;
   *   0.1 = ±10% (default 0.1).
   * @param options.retryOn - Predicate; return `false` to fail immediately
   *   (default: retry all errors).
   * @throws The last `CoreError` if `maxAttempts` reached or `retryOn`
   *   returns `false`.
   */
  protected async withRetry<T>(
    operation: () => Promise<T>,
    options: {
      minBackoff?: number
      maxBackoff?: number
      maxAttempts?: number
      jitter?: number
      retryOn?: (error: CoreError) => boolean
    } = {},
  ): Promise<T> {
    const {
      minBackoff = 1000,
      maxBackoff = 60000,
      maxAttempts = Infinity,
      jitter = 0.1,
      retryOn = () => true,
    } = options

    let attempt = 0
    let backoff = minBackoff

    while (true) {
      attempt++
      try {
        return await operation()
      } catch (err) {
        const error = CoreError.from(err, 'RETRY_ERROR')

        if (attempt >= maxAttempts || !retryOn(error)) {
          throw error
        }

        const jitterMs = backoff * jitter * (Math.random() * 2 - 1)
        const sleepMs = Math.min(backoff + jitterMs, maxBackoff)

        this.debug('withRetry.backoff', {
          attempt,
          backoffMs: Math.round(sleepMs),
          nextAttempt: attempt + 1,
          error: error.code,
        })

        // Per-attempt counter + backoff distribution, labelled by error code
        // so dashboards can spot a flaky failure mode (e.g. an `ECONNRESET`
        // storm) without diving into logs.
        this.counter(RETRY_ATTEMPT_TOTAL, { code: error.code })
        this.histogram(RETRY_BACKOFF_MS, Math.round(sleepMs), {
          code: error.code,
        })

        await this.sleep(sleepMs)

        backoff = Math.min(backoff * 2, maxBackoff)
      }
    }
  }

  /**
   * Wrap a promise with a timeout. On expiry rejects with
   * `CoreError({ code: 'TIMEOUT' })` so callers can branch on `err.code`
   * (a bare `Error` left them substring-matching `'timed out'`), and emits a
   * `warn` log + `timeout_total{name}` counter.
   *
   * @param name - Operation name; also the `name` label on the counter, so
   *   keep cardinality low (constant, not a per-request id).
   */
  protected withTimeout<T>(
    promise: Promise<T>,
    ms: number,
    name: string,
  ): Promise<T> {
    // `clearTimeout` in `finally` is load-bearing: without it the rejection
    // timer keeps node's event loop alive for `ms` after `promise` resolves,
    // blocking mocha (and other reporters that wait for a clean event-loop
    // drain) from exiting until the timer fires.
    let timer: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<T>((_, reject) => {
      timer = setTimeout(() => {
        const err = new CoreError({
          code: 'TIMEOUT',
          message: `${name} timed out after ${ms}ms`,
          metadata: { name, timeoutMs: ms },
        })
        this.warn('timeout', err, { name, timeoutMs: ms })
        this.counter(TIMEOUT_TOTAL, { name })
        reject(err)
      }, ms)
    })
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
  }

  /**
   * Wrap an async operation with timer + counter metrics. Records:
   *   • `{operation}.duration` histogram (ms, with `success` label)
   *   • `{operation}.total` counter (with `success` label)
   *
   * `parseMetricName` brands the dynamic names and throws
   * `INVALID_METRIC_NAME` synchronously on a malformed `operation` —
   * surfacing the typo at the call site rather than letting it slip into
   * the Prometheus exposition.
   *
   * Public (vs `withRetry` / `withTimeout`'s protected) so a composing
   * caller can wrap operations in another instance's metrics from outside
   * its class hierarchy (e.g. when telemetry should be reported on a
   * coordinator rather than the worker actually performing the work).
   */
  async withMetrics<T>(
    operation: string,
    fn: () => Promise<T>,
    labels?: MetricLabels,
  ): Promise<T> {
    const durationName = parseMetricName(`${operation}.duration`)
    const totalName = parseMetricName(`${operation}.total`)
    const done = this.timer(durationName)

    try {
      const result = await fn()
      done({ ...labels, success: 'true' })
      this.counter(totalName, { ...labels, success: 'true' })
      return result
    } catch (err) {
      done({ ...labels, success: 'false' })
      this.counter(totalName, { ...labels, success: 'false' })
      throw err
    }
  }

  /** True if a log at `level` should be emitted (has listeners + above threshold). */
  private shouldLog(level: number): boolean {
    if (!this.events.has('log')) return false
    return level >= this.getEffectiveLevel()
  }

  /** True if metric events have any listeners. */
  private shouldEmitMetric(): boolean {
    return this.events.has('metric')
  }
}
